import { History, RefreshCw, Search } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

import { listAuditLogs, type AuditLog } from "@/services/audit";

const ACTION_LABELS: Record<string, string> = {
  "app.create": "创建应用",
  "app.update": "更新应用",
  "app.delete": "删除应用",
  "app.export": "导出应用",
  "app.apply_data_models": "同步数据模型",
  "workflow.execute": "执行工作流",
  "ai.generate_app": "AI 生成应用",
  "file.upload": "上传文件",
  "file.delete": "删除文件",
};

const ACTION_COLORS: Record<string, string> = {
  "app.create": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
  "app.update": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100",
  "app.delete": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
  "app.export": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100",
  "app.apply_data_models": "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100",
  "workflow.execute": "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-100",
  "ai.generate_app": "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-100",
  "file.upload": "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-100",
  "file.delete": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
};

function formatAction(action: string) {
  return ACTION_LABELS[action] || action;
}

function ActionBadge({ action }: { action: string }) {
  return (
    <Badge className={ACTION_COLORS[action] || "bg-muted"} variant="secondary">
      {formatAction(action)}
    </Badge>
  );
}

export function AuditLogPanel() {
  const [logs, setLogs] = React.useState<AuditLog[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAuditLogs({ limit: 100 });
      setLogs(data.logs);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("加载审计日志失败", error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const filtered = logs.filter(
    (log) =>
      log.action.toLowerCase().includes(query.toLowerCase()) ||
      (log.resource_id ?? "").toLowerCase().includes(query.toLowerCase()) ||
      (log.actor_id ?? "").toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <History className="h-4 w-4" />
            审计日志
          </span>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={cn("mr-1 h-3.5 w-3.5", loading && "animate-spin")} />
            刷新
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="搜索操作、资源 ID、用户 ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 pl-7 text-xs"
          />
        </div>
        <div className="flex-1 overflow-auto rounded-md border">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">暂无审计日志</div>
          )}
          <div className="divide-y">
            {filtered.map((log) => (
              <div key={log.id} className="p-3 text-sm hover:bg-accent">
                <div className="flex items-center gap-2">
                  <ActionBadge action={log.action} />
                  <span className="text-xs text-muted-foreground">
                    {new Date(log.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="mt-1 grid gap-1 text-xs text-muted-foreground">
                  {log.actor_id && <p>操作人：{log.actor_id}</p>}
                  {log.resource_type && <p>资源类型：{log.resource_type}</p>}
                  {log.resource_id && <p>资源 ID：{log.resource_id}</p>}
                  {Object.keys(log.details).length > 0 && (
                    <pre className="mt-1 max-h-24 overflow-auto rounded bg-muted p-1.5 font-mono text-[10px]">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
